import { StreamChat } from "stream-chat";
import type { ChatTokenData } from "Api/Queries/Chat/useChatTokenQuery";
import { removeChatPushDevice } from "Infrastructure/Capacitor/chatPushDevice";
import { ConnectSupersededError } from "Utils/ConnectSupersededError";

/**
 * Thrown when a newer connect (persona switch, logout, query cancellation) superseded this one,
 * or when a token refresh finds the active persona changed underneath the connection.
 */
export class ChatConnectSupersededError extends ConnectSupersededError {
  constructor(message = "Chat connect superseded") {
    super(message);
    this.name = "ChatConnectSupersededError";
  }
}

type ConnectedStreamChat = {
  /** apiKey + Stream user id — a different pair means a different persona and forces a reconnect. */
  key: string;
  client: StreamChat;
  clientPromise: Promise<StreamChat>;
};

let current: ConnectedStreamChat | null = null;

/**
 * Connects the Stream Chat client once per persona — the SINGLE chat WebSocket shared by the chat
 * page and the always-mounted unread badge (two clients for the same user would mean two sockets
 * and double event delivery). Module-level singleton for the same reason as the stories feed
 * client: effects can run twice (StrictMode) and remounts must reuse the existing connection.
 *
 * The token is supplied as a PROVIDER, not a string: the SDK only refreshes expired tokens
 * (WS token-expired reconnect, REST 401 retry) when the token is non-static, and it also calls
 * the provider for the initial connect — so every token in play is fresh at call time. It also
 * keeps the token string out of the connection identity: a background token refetch must not
 * tear the socket down.
 *
 * Every await in the success path re-checks that this connect is still the current one; a logout
 * or persona switch that lands mid-connect aborts it and disconnects the client instead of
 * leaving an untracked, connected WebSocket behind.
 */
export const connectStreamChat = (
  chatToken: ChatTokenData,
  tokenProvider: () => Promise<string>,
  signal?: AbortSignal,
): Promise<StreamChat> => {
  const key = `${chatToken.apiKey}:${chatToken.userId}`;
  if (current?.key === key) {
    return current.clientPromise;
  }

  const previous = current;
  const client = new StreamChat(chatToken.apiKey);
  const clientPromise = (async () => {
    // Only valid after an await: the IIFE body runs synchronously up to its first await, i.e.
    // BEFORE `current = { key, client, clientPromise }` below — a synchronous check would always
    // see a foreign `current` and abort every fresh connect.
    const assertNotSuperseded = () => {
      if (current?.client !== client || signal?.aborted) {
        throw new ChatConnectSupersededError();
      }
    };

    try {
      // Persona switch: drop the previous identity's connection first. Its push device goes
      // with it — Stream does not document that re-adding a token under another user removes it
      // from the first, so the previous persona's pushes would keep landing on this phone. The
      // previous client is still authenticated here; after `disconnectUser` it could not do this.
      // No-op outside the native shells (no stored token).
      if (previous) {
        await removeChatPushDevice(previous.client);
        await previous.client.disconnectUser().catch(() => undefined);
        assertNotSuperseded();
      }
      // Id ONLY. Stream applies whatever user fields a connect carries to the profile, and the
      // profile is the backend's to write (ADR-0007): it holds the @handle for a private persona,
      // and a connect carrying the real name would overwrite that for every other viewer until
      // the next backend upsert flipped it back — the own-name flicker, and a leak meanwhile.
      // The token's userName is for this client's own rendering (useDisclosedNameResolver).
      await client.connectUser({ id: chatToken.userId }, tokenProvider);
      assertNotSuperseded();
      return client;
    } catch (error) {
      // Do not cache a failed connection — the next call retries from scratch. On supersession
      // this disconnect is the zombie kill: the client may have fully connected in the meantime.
      if (current?.client === client) {
        current = null;
      }
      await client.disconnectUser().catch(() => undefined);
      throw error;
    }
  })();

  current = { key, client, clientPromise };
  return clientPromise;
};

/**
 * Logout teardown. Deliberately does NOT remove the push device: the client may not be connected
 * at logout (connect still pending, failed, or a chat-less active persona), so the API does that
 * server-side for every persona — see `removeChatPushDeviceRequest`.
 */
export const disconnectStreamChat = async (): Promise<void> => {
  if (!current) {
    return;
  }
  const { client } = current;
  current = null;
  await client.disconnectUser().catch(() => undefined);
};
